/**
 * The native shell, when there is one.
 *
 * `apps/shell` loads this application from its hosted URL, so exactly the same
 * bundle runs in a plain browser and inside the Tauri window. Everything here
 * therefore answers *maybe*: `inShell()` is false in a browser, and every
 * caller must have a path for that. The shell is an enhancement to a kiosk that
 * already works, never a requirement.
 *
 * ── Why the global and not `@tauri-apps/api` ──────────────────────────────
 *
 * The shell sets `withGlobalTauri`, so `window.__TAURI__` is injected into the
 * page. Reading it directly keeps `@tauri-apps/api` out of `package.json`,
 * which matters more than the kilobytes: this bundle is what a browser runs,
 * and it should not carry code for a host it does not have. The cost is that
 * the shapes below are asserted rather than imported, so they are kept narrow
 * and each is checked before it is used.
 *
 * ── The command names are a contract with `capabilities/default.json` ─────
 *
 * A command absent from the shell's capability is refused at runtime with a
 * message the page never shows anybody. Adding one here means adding it to
 * `apps/shell/build.rs` and to the capability in the same change.
 */

interface TauriCore {
  invoke?: (command: string, args?: Record<string, unknown>) => Promise<unknown>
}

interface TauriEvent {
  listen?: (event: string, handler: (message: { payload: unknown }) => void) => Promise<() => void>
}

interface TauriGlobal {
  core?: TauriCore
  event?: TauriEvent
}

function tauri(): TauriGlobal | undefined {
  const global = (globalThis as { __TAURI__?: unknown }).__TAURI__
  return typeof global === 'object' && global !== null ? (global as TauriGlobal) : undefined
}

/** True inside the native shell, false in every browser. */
export function inShell(): boolean {
  return typeof tauri()?.core?.invoke === 'function'
}

async function invoke<T>(command: string, args?: Record<string, unknown>): Promise<T> {
  const call = tauri()?.core?.invoke
  if (call === undefined) throw new Error(`not running in the shell, so ${command} is unavailable`)
  return (await call(command, args)) as T
}

/** `serde` gives snake_case on the wire. */
interface ShellInfoWire {
  readonly version: string
  readonly platform: string
  readonly arch: string
  readonly identifier: string
  readonly window_url: string
  readonly printer: string | null
  readonly printer_kind: string
  readonly printer_from_env: boolean
  readonly receipt_mode: string
  readonly receipt_dots: number
  readonly receipt_columns: number
  readonly receipt_codepage: number
  readonly terminals: readonly string[]
  readonly relay: string | null
  readonly log_path: string | null
  readonly config_path: string | null
  readonly config_source: string
  readonly problems: readonly string[]
  readonly updater_configured: boolean
  readonly kiosk: boolean
  readonly exit_locked: boolean
  readonly devtools: boolean
}

export interface ShellInfo {
  readonly version: string
  readonly platform: string
  readonly arch: string
  /** Keys the WebView2 user-data folder, and so the IndexedDB the outbox lives in. */
  readonly identifier: string
  readonly windowUrl: string
  /**
   * True when this machine has a printer configured — **including one whose
   * address is a typo**. Deliberate: a target the shell cannot parse should
   * produce an attempt, a failure and a banner, not a silent fall back to the
   * browser that leaves a store believing the printer they bought is in use.
   */
  readonly printer: boolean
  /** The address itself, so support can see a typo rather than infer one. */
  readonly printerTarget: string | undefined
  /** `tcp` | `cups` | `winspool` | `usb` | `serial` | `invalid` | `none`. */
  readonly printerKind: string
  /**
   * `MGC_PRINTER` is set, and it is overriding whatever the file says.
   *
   * The variable wins for the run: a selection made on this screen is written
   * to `kiosk.toml` and takes effect at the next restart. Without saying so the
   * button appears to do nothing.
   */
  readonly printerFromEnv: boolean
  readonly receipt: { readonly mode: 'raster' | 'text'; readonly dots: number }
  readonly receiptColumns: number
  readonly receiptCodepage: number
  /**
   * The terminal ids this machine has, from `kiosk.toml` — not a count.
   * A shop with two counters on one kiosk needs to say which.
   */
  readonly terminals: readonly string[]
  /** The LAN address the shell will relay a path to, when one is configured. */
  readonly relay: string | undefined
  readonly logPath: string | undefined
  readonly configPath: string | undefined
  readonly configSource: string
  /** What was wrong with the machine's configuration. Empty when healthy. */
  readonly problems: readonly string[]
  readonly updaterConfigured: boolean
  readonly kiosk: boolean
  /**
   * True when a PIN closes this kiosk.
   *
   * False while `kiosk` is true means the machine has no `exit_pin_sha256` and
   * therefore cannot be closed from the screen at all — the safe direction, and
   * a surprising one, so the settings screen says it. The digest itself never
   * crosses; there is nothing to read off this screen.
   */
  readonly exitLocked: boolean
  readonly devtools: boolean
}

/**
 * Asked once and remembered.
 *
 * Every field here is fixed for the life of the shell process, and the callers
 * that matter are on the path of a sale — a receipt must not wait on an IPC
 * round trip to discover there is a printer.
 *
 * **That is a rule, not an observation.** A field that can change while the
 * process runs does not belong on `shell_info`, with exactly two exemptions,
 * and each is a field whose writer invalidates the cache below. The printer
 * target has two writers — `selectPrinter` here, and the shell's own setup
 * window, which announces on `shell://printer-changed`. The lock's writers are in
 * the shell — its system menu locks the window and `shell_unlock` unlocks it
 * after a PIN — so it announces instead, on `shell://kiosk-changed`, and the
 * listener a few lines down is what makes that an invalidation rather than a
 * settings screen describing a machine that no longer exists.
 */
let cached: Promise<ShellInfo | undefined> | undefined

export function forgetShellInfo(): void {
  cached = undefined
}

export function shellInfo(): Promise<ShellInfo | undefined> {
  cached ??= load()
  return cached
}

/**
 * The lock changed under us, so what is cached is a machine that no longer
 * exists.
 *
 * Subscribed once, on the first `shellInfo()`, rather than at module load: in a
 * plain browser there is nothing to subscribe to, and `listen` is a no-op there
 * anyway — this just keeps the import free of a side effect. Forgetting is the
 * whole of it; a screen that wants the new value asks for it, and
 * `screens/settings.tsx` listens for the same event to know when to.
 */
let watching = false

function watchTheLock(): void {
  if (watching) return
  watching = true
  // Every writer the shell owns: the lock, the terminal list, and the printer
  // target when the shell's own setup window is what chose it. None of those
  // goes through this page, so none can invalidate the cache the way
  // `selectPrinter` does — the shell says so instead.
  for (const event of ['shell://kiosk-changed', 'shell://terminals-changed', 'shell://printer-changed'] as const) {
    void listen(event, () => {
      forgetShellInfo()
    })
  }
}

async function load(): Promise<ShellInfo | undefined> {
  if (!inShell()) return undefined
  watchTheLock()
  try {
    const wire = await invoke<ShellInfoWire>('shell_info')
    return {
      version: wire.version,
      platform: wire.platform,
      arch: wire.arch,
      identifier: wire.identifier,
      windowUrl: wire.window_url,
      printer: wire.printer !== null,
      printerTarget: wire.printer ?? undefined,
      printerKind: wire.printer_kind,
      printerFromEnv: wire.printer_from_env,
      receipt: {
        mode: wire.receipt_mode === 'text' ? 'text' : 'raster',
        dots: wire.receipt_dots
      },
      receiptColumns: wire.receipt_columns,
      receiptCodepage: wire.receipt_codepage,
      terminals: wire.terminals,
      relay: wire.relay ?? undefined,
      logPath: wire.log_path ?? undefined,
      configPath: wire.config_path ?? undefined,
      configSource: wire.config_source,
      problems: wire.problems,
      updaterConfigured: wire.updater_configured,
      kiosk: wire.kiosk,
      exitLocked: wire.exit_locked,
      devtools: wire.devtools
    }
  } catch {
    // A shell whose `shell_info` is refused by the ACL is a misconfiguration,
    // not a browser. Both end up on the same degraded path, and the settings
    // screen shows the shell as present with no capabilities.
    return undefined
  }
}

// ── Printing ──────────────────────────────────────────────────────────────

export async function printRaw(bytes: Uint8Array): Promise<void> {
  await invoke('shell_print_raw', { bytes: Array.from(bytes) })
}

export interface PrinterStatus {
  readonly online: boolean | null
  readonly paperOut: boolean | null
  readonly coverOpen: boolean | null
  readonly error: boolean | null
  /** Set when the transport cannot read back — `lp://` and `win://`. */
  readonly unavailable: boolean
}

/**
 * `DLE EOT`, where the wire reads back.
 *
 * Every field is `Option` on the Rust side and nullable here, because "the
 * printer did not say" stays distinct from "no" all the way to the screen.
 * Plenty of cheap units implement printing and not status, so **silence is an
 * ordinary outcome** — not an error and not a retry.
 */
export async function printerStatus(): Promise<PrinterStatus | undefined> {
  if (!inShell()) return undefined
  try {
    return await invoke<PrinterStatus>('shell_printer_status')
  } catch {
    return undefined
  }
}

export interface Device {
  readonly id: string
  readonly label: string
  readonly source: 'queue' | 'serial' | 'usb' | 'network'
  readonly wire: string
  readonly selectable: boolean
  readonly detail: string | null
}

export const listDevices = (deep = false): Promise<Device[]> => invoke('shell_devices', { deep })

/**
 * Choose a printer.
 *
 * What crosses is `"7-3"` — the third device of the seventh scan this process
 * performed — never an address. The shell looks it up in the table it built by
 * asking the operating system and refuses a stale generation or an unknown
 * index. A command that took an address would make every kiosk a write
 * primitive into the store's LAN for anyone who compromised the deployment.
 */
export async function selectPrinter(deviceId: string): Promise<void> {
  await invoke('shell_select_printer', { deviceId })
  // The one moving field on `shell_info`, and this is the writer that owns it.
  forgetShellInfo()
}

// ── The card terminal ─────────────────────────────────────────────────────

/**
 * What the terminal said — in four values, not two.
 *
 * This was `approved: boolean`, and the missing third state cost real money.
 * A card is charged on the counter by hardware that does not know the internet
 * exists, so when the shell stops waiting it does not know whether the sale
 * completed. Folding that onto `false` told this page "no money taken", the page
 * wrote no outbox entry, and the charge existed nowhere at all.
 *
 * - `approved`  — money has been taken. Write the claim.
 * - `completed` — a probe or cancel succeeded. No money moved. Distinct from
 *                 `approved` because both come back `00`.
 * - `declined`  — the terminal refused, or nothing was ever sent. No money.
 * - `unknown`   — **the card may have been charged.** Write the claim anyway.
 */
export type TerminalOutcome = 'approved' | 'completed' | 'declined' | 'unknown'

export interface TerminalResult {
  readonly outcome: TerminalOutcome
  readonly approvalCode: string | null
  readonly maskedPan: string | null
  readonly message: string
  readonly code: string | null
}

/** Only a sale that took money. Never a probe, never an ambiguity. */
export const tookMoney = (result: TerminalResult): boolean => result.outcome === 'approved'

/**
 * Whether a claim must be written before anything else happens.
 *
 * The single most important predicate in the page. If this is true, the outbox
 * write is awaited before the success screen, before the receipt, before the
 * POST — see `docs/offline.md`.
 */
export const mayHaveTakenMoney = (result: TerminalResult): boolean =>
  result.outcome === 'approved' || result.outcome === 'unknown'

/**
 * Charge a card.
 *
 * Long by design — the terminal is given three minutes and the shell waits
 * longer than that — because a customer inserting a card, choosing an account
 * and entering a PIN takes as long as it takes. What must not be long is the
 * *screen's* patience: the modal is cancellable throughout and `cancelTerminal`
 * is always reachable, which is true now that the shell runs this off the UI
 * thread rather than on the thread that also dispatches the cancel.
 */
export const terminalPay = (terminalId: string, amount: number): Promise<TerminalResult> =>
  invoke('shell_terminal_pay', { terminalId, amount })

export const cancelTerminal = (terminalId: string): Promise<TerminalResult> =>
  invoke('shell_terminal_cancel', { terminalId })

export const probeTerminal = (terminalId: string): Promise<TerminalResult> =>
  invoke('shell_terminal_probe', { terminalId })

export const scanTerminals = (): Promise<Device[]> => invoke('shell_terminal_scan')

// ── Secrets, logging, the window ──────────────────────────────────────────

export const secretGet = (key: string): Promise<string | null> => invoke('shell_secret_get', { key })
export const secretSet = (key: string, value: string): Promise<void> => invoke('shell_secret_set', { key, value })
export const secretRemove = (key: string): Promise<void> => invoke('shell_secret_remove', { key })

/** A line into the shell's own rotating log, so a support bundle has both halves. */
export function shellLog(level: 'info' | 'warn' | 'error', message: string): void {
  if (!inShell()) return
  void invoke('shell_log', { level, message }).catch(() => {})
}

export const reload = (): Promise<void> => invoke('shell_reload')

/**
 * Ask the shell for the exit keypad.
 *
 * **The gesture for a machine with no keyboard.** A locked kiosk has no close
 * box, no taskbar and — on Windows, which is the fleet — no menu bar either, so
 * every route back out was a key combination on a screen that may have nothing
 * plugged into it. Ten taps on the wordmark is the route a person standing at
 * the counter has.
 *
 * It opens a pad and nothing else. The PIN is typed into a window the *shell*
 * serves and compared in Rust, so this call is not a way out of kiosk mode and
 * cannot be made into one; outside the shell there is no pad and the promise
 * rejects, which is the same nothing every other native call degrades to.
 */
export const requestExit = (): Promise<void> => invoke('shell_request_exit')

export const setTheme = (theme: 'light' | 'dark' | null): Promise<void> => invoke('shell_set_theme', { theme })

/**
 * Relay a **path** to the machine's configured LAN address.
 *
 * Never a URL. A command that took one would make every kiosk an open proxy
 * into the store's network for anyone who compromised the deployment. This
 * should be unused on most machines — the primary path to 1C is the server.
 */
export const relay = (path: string, init?: { method?: string; body?: string }): Promise<string> =>
  invoke('shell_relay', {
    path,
    method: init?.method ?? 'GET',
    body: init?.body ?? null
  })

// ── e-Barimt ──────────────────────────────────────────────────────────────

export interface ShellCompany {
  readonly tin: string
  readonly name: string
}

/**
 * A company registration number to a name, through the shell.
 *
 * **The one native call that exists for a geographic reason.** e-Barimt answers
 * this lookup only from inside Mongolia and `apps/server` runs in Germany, so
 * the till is the only part of this system standing in the right country. The
 * page cannot make the request itself whatever the geography: e-Barimt pins
 * `Access-Control-Allow-Origin` to `https://developer.itc.gov.mn`, which is the
 * same wall the Electron tree answered with `hardware:resolve-ebarimt-company`.
 *
 * What crosses is digits. The URL is a constant in `apps/shell/src/ebarimt.rs`,
 * so this is not the open proxy `relay` above is careful not to be.
 *
 * It rejects with a **refusal code** — `company-not-found` or
 * `ebarimt-unavailable`, the two `packages/i18n` already carries — so the
 * caller renders it exactly as it renders the server's refusal, and the
 * distinction that matters survives: a customer whose number is right and
 * whose e-Barimt is down must not be told the number is wrong.
 */
export const companyLookup = (registrationNumber: string): Promise<ShellCompany> =>
  invoke('shell_company_lookup', { regNo: registrationNumber })

// ── Events ────────────────────────────────────────────────────────────────

export type ShellEvent =
  /**
   * Raw-HID scanners, for units the keyboard wedge cannot read.
   *
   * **Nothing emits this**, and there is no HID module in `apps/shell`. It is
   * kept as a declared shape rather than deleted because D7 names it as the
   * escape hatch for units that do not present as a keyboard — but a listener
   * on it will never fire today, and `src/shell/wedge.ts` is what actually
   * reads a scanner. If a shop turns up with a raw-HID unit, this is the
   * contract to implement against; until then it is a promise, not a feature.
   */
  | { readonly event: 'shell://scanner'; readonly payload: string }
  /** Also unemitted. `printerStatus()` is a command, and that is the live path. */
  | {
      readonly event: 'shell://printer-status'
      readonly payload: PrinterStatus
    }
  | {
      readonly event: 'shell://close-requested'
      readonly payload: { id: number }
    }
  | {
      readonly event: 'shell://reload-requested'
      readonly payload: { id: number }
    }
  | {
      readonly event: 'shell://update-ready'
      readonly payload: { version: string }
    }
  /**
   * The window was locked or unlocked while the process ran.
   *
   * The shell's system menu locks it and `shell_unlock` unlocks it after the
   * exit PIN — neither is a thing the page can ask for, and both change
   * `kiosk` and `exitLocked` on the otherwise-cached `shellInfo()`. The cache
   * drops itself; a screen that draws either field listens for this to know
   * when to read again.
   */
  | {
      readonly event: 'shell://kiosk-changed'
      readonly payload: { kiosk: boolean }
    }
  /**
   * A terminal was added, changed or removed from the shell's setup window.
   *
   * `terminals` on the otherwise-cached `shellInfo()` has changed, and it is
   * what the payment screen draws its chooser from — so a terminal added at the
   * counter has to reach the till without a restart.
   */
  | { readonly event: 'shell://terminals-changed'; readonly payload: null }
  /**
   * The printer target changed, and this page is not who changed it.
   *
   * `selectPrinter` invalidates the cache on this side, so this event is about
   * the *other* caller of the same command: the shell's setup window, the one
   * an integrator uses with the machine in front of them. Without it a printer
   * chosen at the counter reached the till only after a reload, and until then
   * `printSlip` read a cached `printer: null` and sent every receipt to
   * `window.print()` — a working printer, sitting idle, next to a browser
   * dialog nobody at a kiosk can dismiss.
   */
  | { readonly event: 'shell://printer-changed'; readonly payload: null }
  /**
   * The shell's setup window pressed «Туршилтын хэвлэлт» and needs a slip.
   *
   * The shell cannot draw one: `packages/receipt` runs in the page and the
   * shell only carries bytes. So the window asks, this page renders the same
   * slip the settings screen and the console print, and `testSlipDrawn` hands
   * the bytes back against the nonce. A page that does not answer within the
   * shell's four seconds gets the shell's own ASCII slip printed instead —
   * which is the whole reason that slip still exists, because the page is
   * exactly what may not be running when somebody is setting a machine up.
   */
  | {
      readonly event: 'shell://test-slip-requested'
      readonly payload: { id: number }
    }

export async function listen<T>(event: string, handler: (payload: T) => void): Promise<() => void> {
  const subscribe = tauri()?.event?.listen
  if (subscribe === undefined) return () => {}
  return subscribe(event, (message) => handler(message.payload as T))
}

export const closeVerdict = (id: number, close: boolean): Promise<void> => invoke('shell_close_verdict', { id, close })

export const reloadVerdict = (id: number, doReload: boolean): Promise<void> =>
  invoke('shell_reload_verdict', { id, reload: doReload })

/**
 * The slip the setup window asked for, or `null` for «I could not draw one».
 *
 * `null` is not a silence: it is the same fallback four seconds of silence
 * would buy, taken immediately, so an integrator is not left watching a dead
 * printer plate while a page that already failed runs out a timeout.
 */
export const testSlipDrawn = (id: number, bytes: Uint8Array | null): Promise<void> =>
  invoke('shell_test_slip', {
    id,
    bytes: bytes === null ? null : Array.from(bytes)
  })

// ── The shell's own updater ───────────────────────────────────────────────

export interface UpdateStatus {
  readonly held: boolean
  readonly version: string | null
  readonly installing: boolean
  readonly configured: boolean
}

export const updateStatus = (): Promise<UpdateStatus> => invoke('shell_update_status')
export const applyUpdate = (): Promise<void> => invoke('shell_apply_update')
